/* OwnerAdminFunnelAnalytics — conversion funnel dashboard for CountBucks owner.
 *
 * Visualizes the registration → checkout funnel powered by `funnel_events`
 * Mongo collection. Useful for measuring:
 *   • Plan-tier distribution (free / starter / pro / business / platinum)
 *   • Annual vs monthly cadence split
 *   • Social-proof banner click-through rate
 *   • Drop-off between landing → register → checkout → payment
 *
 * Source data fed by /app/frontend/src/lib/funnelTracker.js fired from
 * LandingPageDark, LoginDark, BillingSettings.
 */
import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarPicker } from '../ui/calendar';
import {
  BarChart3, Calendar, Loader2, Sparkles, TrendingUp, Users, Zap, Check,
  MailWarning, Send, HeartHandshake,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Same YYYY-MM-DD formatter used on the Traffic dashboard so both pages
// agree on how a "day" is defined.
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PLAN_LABELS = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  platinum: 'Platinum CFO',
};
const PLAN_COLORS = {
  free: 'bg-zinc-500',
  starter: 'bg-blue-500',
  pro: 'bg-emerald-500',
  business: 'bg-violet-500',
  platinum: 'bg-amber-500',
};

const RANGE_OPTIONS = [
  { days: 1,   label: 'Today' },
  { days: 7,   label: '7d' },
  { days: 30,  label: '30d' },
  { days: 90,  label: '90d' },
];

export default function OwnerAdminFunnelAnalytics() {
  const [days, setDays] = useState(30);
  // Custom-range popover state. When `customRange.from`+`to` are set they
  // win over the `days` preset (see load effect below).
  const [customRange, setCustomRange] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Query string used by both the funnel + abandoned-signup endpoints so
  // they always agree on the window.
  const rangeQS = useMemo(() => {
    if (customRange?.from && customRange?.to) {
      return `start_date=${toISODate(customRange.from)}&end_date=${toISODate(customRange.to)}`;
    }
    return `days=${days}`;
  }, [days, customRange?.from, customRange?.to]);

  const rangeLabel = customRange?.from && customRange?.to
    ? `${toISODate(customRange.from)} → ${toISODate(customRange.to)}`
    : 'Custom range';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiFetch(`${API_URL}/api/owner-admin/funnel-analytics?${rangeQS}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text().catch(() => 'Failed to load'));
        return r.json();
      })
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [rangeQS]);

  const totals = useMemo(() => {
    if (!data) return { plan: 0, monthly: 0, annual: 0, completed: 0, social: 0 };
    const plan = (data.plan_breakdown || []).reduce((s, p) => s + p.count, 0);
    const mo = (data.cadence_breakdown || []).find((c) => c.cadence === 'monthly')?.count || 0;
    const yr = (data.cadence_breakdown || []).find((c) => c.cadence === 'annual')?.count || 0;
    const completed = (data.funnel_steps || []).find((s) => s.event === 'checkout.completed')?.count || 0;
    return { plan, monthly: mo, annual: yr, completed, social: data.social_proof_clicks || 0 };
  }, [data]);

  // Top-of-funnel = the FIRST step in funnel_steps. Drives conversion %.
  const topCount = data?.funnel_steps?.[0]?.count || 0;
  const pct = (n) => (topCount > 0 ? Math.round((n / topCount) * 100) : 0);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="funnel-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <BarChart3 className="w-6 h-6 text-emerald-500" />
            Funnel Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Registration → checkout conversion funnel. Powered by client-side event tracking.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {RANGE_OPTIONS.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={!customRange && days === r.days ? 'default' : 'outline'}
              onClick={() => { setCustomRange(null); setDays(r.days); }}
              data-testid={`range-${r.days}d`}
            >
              {r.label}
            </Button>
          ))}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant={customRange ? 'default' : 'outline'}
                data-testid="funnel-range-custom"
                className="gap-1.5"
              >
                <Calendar className="h-3.5 w-3.5" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0" data-testid="funnel-range-picker">
              <CalendarPicker
                mode="range"
                selected={customRange || undefined}
                onSelect={(r) => {
                  setCustomRange(r || null);
                  if (r?.from && r?.to) setPickerOpen(false);
                }}
                numberOfMonths={2}
                initialFocus
              />
              <div className="flex items-center justify-between border-t p-2 gap-2">
                <span className="text-[11px] text-muted-foreground pl-2">
                  Pick a start and end date
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setCustomRange(null); setPickerOpen(false); }}
                  data-testid="funnel-range-clear"
                >
                  Clear
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading funnel data…
        </div>
      )}
      {error && !loading && (
        <Card className="border-red-500/30">
          <CardContent className="p-4 text-sm text-red-500">{error}</CardContent>
        </Card>
      )}

      {data && !loading && (
        <>
          {/* ─── Top-line KPIs ──────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <KpiCard
              icon={Users}
              label="Plans selected"
              value={totals.plan.toLocaleString()}
              hint={customRange ? rangeLabel : (RANGE_OPTIONS.find((r) => r.days === days)?.label)}
              data-testid="kpi-plans"
            />
            <KpiCard
              icon={TrendingUp}
              label="Checkouts completed"
              value={totals.completed.toLocaleString()}
              hint={`${pct(totals.completed)}% of top of funnel`}
              data-testid="kpi-completed"
            />
            <KpiCard
              icon={Calendar}
              label="Annual cadence"
              value={`${totals.annual.toLocaleString()}`}
              hint={`${totals.monthly + totals.annual > 0 ? Math.round((totals.annual / (totals.monthly + totals.annual)) * 100) : 0}% of plan selections`}
              data-testid="kpi-annual"
            />
            <KpiCard
              icon={Sparkles}
              label="Social-proof clicks"
              value={totals.social.toLocaleString()}
              hint="Pro banner conversions"
              data-testid="kpi-social"
            />
          </div>

          {/* ─── Funnel steps ───────────────────────────────────── */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="w-4 h-4 text-emerald-500" /> Conversion funnel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(data.funnel_steps || []).map((step) => {
                const stepPct = pct(step.count);
                return (
                  <div key={step.event} className="space-y-1" data-testid={`funnel-step-${step.event}`}>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-foreground font-medium">{step.label}</span>
                      <span className="text-muted-foreground font-mono">
                        {step.count.toLocaleString()} <span className="text-zinc-500 text-xs">· {stepPct}%</span>
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                        style={{ width: `${stepPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {topCount === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No events yet in the selected range. The funnel starts populating as soon as users click
                  pricing CTAs on the landing page.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ─── Plan distribution + Cadence split ─────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Plan breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.plan_breakdown || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No plan selections yet.</p>
                ) : (
                  (data.plan_breakdown || []).map((p) => {
                    const planPct = totals.plan > 0 ? Math.round((p.count / totals.plan) * 100) : 0;
                    return (
                      <div key={p.plan_id} className="space-y-1" data-testid={`plan-row-${p.plan_id}`}>
                        <div className="flex justify-between text-sm">
                          <span className="text-foreground">{PLAN_LABELS[p.plan_id] || p.plan_id}</span>
                          <span className="text-muted-foreground font-mono">{p.count} · {planPct}%</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full ${PLAN_COLORS[p.plan_id] || 'bg-zinc-500'} transition-all duration-500`} style={{ width: `${planPct}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Monthly vs Annual</CardTitle>
              </CardHeader>
              <CardContent>
                {totals.monthly + totals.annual === 0 ? (
                  <p className="text-xs text-muted-foreground">No cadence data yet.</p>
                ) : (
                  <div className="space-y-4">
                    <CadenceRow label="Monthly" count={totals.monthly} total={totals.monthly + totals.annual} color="bg-blue-500" />
                    <CadenceRow label="Annual (−15%)" count={totals.annual} total={totals.monthly + totals.annual} color="bg-emerald-500" />
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Annual subscribers have ~2× the LTV of monthly. Targeting 35%+ annual share is the SaaS benchmark.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ─── Abandoned Signup Nudges ────────────────────────── */}
          <AbandonedSignupCard rangeQS={rangeQS} />

          {/* ─── Health check / explainer ───────────────────────── */}
          <Card className="border-emerald-500/20 bg-emerald-500/5">
            <CardContent className="p-4 flex gap-3 items-start">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground">How to read this:</strong>{' '}
                Plans selected = top of funnel. Each step below shows how many users made it that far.
                A healthy SaaS funnel converts 1.5–4% of pricing-clicks into paid checkouts.{' '}
                The &ldquo;Social-proof clicks&rdquo; KPI shows how often users on the FREE flow upgraded via the
                in-dropdown banner — a strong indicator that the upsell nudge is working.
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small primitives ────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, hint, ...rest }) {
  return (
    <Card {...rest}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
        </div>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function CadenceRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground font-mono">{count} · {pct}%</span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Abandoned Signup Nudges ─────────────────────────────────────────────
// Shows Resend-powered drip performance: how many nudges we sent in the
// current window, split by stage (A = unverified, B = verified/no-billing)
// and by slot (d1 / d3 / d7). Includes a "Send now" button that force-fires
// the loop for anyone still eligible.
function AbandonedSignupCard({ rangeQS }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiFetch(`${API_URL}/api/owner-admin/abandoned-signup/stats?${rangeQS}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text().catch(() => 'Failed to load'));
        return r.json();
      })
      .then((d) => { if (alive) { setData(d); setLoading(false); setError(null); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [rangeQS, nonce]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/owner-admin/abandoned-signup/run-now`,
        { method: 'POST' },
      );
      if (!r.ok) throw new Error(await r.text().catch(() => 'Trigger failed'));
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="abandoned-signup-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <MailWarning className="w-4 h-4 text-amber-500" />
              Abandoned-signup nudges
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Automated Resend drip that re-engages users who registered but never verified,
              and users who verified but never added billing. Sends at 24h / 72h / 7d.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={runNow}
            disabled={running}
            data-testid="abandoned-run-now-btn"
          >
            {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Run now
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading nudge stats…
          </div>
        )}
        {error && !loading && (
          <div className="text-xs text-red-500" data-testid="abandoned-error">{error}</div>
        )}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat
                icon={Send}
                label="Sent"
                value={data.total_sent}
                hint={`${data.range_days}d window`}
                testid="abandoned-total-sent"
              />
              <MiniStat
                icon={HeartHandshake}
                label="Reactivated"
                value={(data.stage_a?.reactivated || 0) + (data.stage_b?.reactivated || 0)}
                hint={`${data.reactivation_rate_pct}% of sends`}
                testid="abandoned-reactivated"
              />
              <MiniStat
                icon={Users}
                label="Stage A · unverified"
                value={data.stage_a?.total || 0}
                hint={`d1 ${data.stage_a?.d1 || 0} · d3 ${data.stage_a?.d3 || 0} · d7 ${data.stage_a?.d7 || 0}`}
                testid="abandoned-stage-a"
              />
              <MiniStat
                icon={Users}
                label="Stage B · no billing"
                value={data.stage_b?.total || 0}
                hint={`d1 ${data.stage_b?.d1 || 0} · d3 ${data.stage_b?.d3 || 0} · d7 ${data.stage_b?.d7 || 0}`}
                testid="abandoned-stage-b"
              />
            </div>
            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-4">
              <span>Opt-outs (window): <strong className="text-foreground">{data.opt_outs}</strong></span>
              {data.mocked_sends > 0 && (
                <span className="text-amber-500">
                  {data.mocked_sends} send{data.mocked_sends === 1 ? '' : 's'} were MOCKED
                  (RESEND_API_KEY not configured)
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="rounded-lg border border-border/50 bg-muted/20 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-xl font-bold text-foreground">{Number(value || 0).toLocaleString()}</div>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
